// "Rapoarte": one section for the reports of every module (Registratură, ALOP and the modules to come). Each module publishes
// the reports the user may see (GET /api/{module}/reports) and runs them as a table or an Excel file (?format=xlsx).
import { isClMember } from '../consiliu/types';
import { isAchMember } from '../achizitii/types';
import React, { useEffect, useMemo, useState } from 'react';
import { api, qs } from '../api';
import { navigate, useAuth, useRoute } from '../store';
import { DatePicker, EmptyState, todayIso, useToast } from '../ui';
import { Icon } from '../ui/Icon';
import { hasAlop } from '../alop/Alop';
import { hasUrbanism } from '../urbanism/Urbanism';

interface ReportDef { code: string; group: string; title: string; description: string; params: string[]; downloadOnly?: boolean }
interface ReportData { title: string; columns: { label: string; type: 'text' | 'money' | 'number' | 'percent' }[]; rows: any[][]; totals?: any[] | null; count: number }

/** The modules that have reports. A new module adds one line here and GET /api/{module}/reports on its server. */
export const REPORT_MODULES: { code: string; label: string; icon: string; api: string; visible: (roles: string[]) => boolean }[] = [
  { code: 'DMS', label: 'Registratură', icon: 'inbox', api: '/api/documents/reports', visible: () => true },
  { code: 'ALOP', label: 'ALOP', icon: 'coins', api: '/api/alop/reports', visible: roles => hasAlop(roles) },
  { code: 'URB', label: 'Urbanism', icon: 'building', api: '/api/urbanism/reports', visible: roles => hasUrbanism(roles) },
  { code: 'ACH', label: 'Achiziții', icon: 'clipboard', api: '/api/achizitii/reports', visible: roles => isAchMember(roles) },
  { code: 'CL', label: 'Consiliul local', icon: 'book', api: '/api/consiliu/reports', visible: roles => isClMember(roles) },
];

const fmt = (v: any, t: string) => {
  if (v == null || v === '') return '';
  if (t === 'money') return Number(v).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (t === 'percent') return Number(v).toLocaleString('ro-RO', { maximumFractionDigits: 1 }) + '%';
  if (t === 'number') return Number(v).toLocaleString('ro-RO', { maximumFractionDigits: 1 });
  return String(v);
};

export function ReportsPage() {
  const toast = useToast();
  const { user } = useAuth();
  const route = useRoute();
  const mods = REPORT_MODULES.filter(m => m.visible(user!.roles));
  const [catalog, setCatalog] = useState<Record<string, ReportDef[]>>({});
  const mod = mods.find(m => m.code === route.params.get('m'))?.code || mods[0].code;
  const code = route.params.get('r') || '';
  useEffect(() => {
    mods.forEach(m => api.get<ReportDef[]>(m.api).then(l => setCatalog(c => ({ ...c, [m.code]: l }))).catch(() => setCatalog(c => ({ ...c, [m.code]: [] }))));
  }, []);
  const list = catalog[mod] || [];
  const def = list.find(d => d.code === code) || null;
  const api0 = mods.find(m => m.code === mod)!.api;
  const year0 = new Date().getFullYear();
  const [year, setYear] = useState(year0);
  const [from, setFrom] = useState<string | null>(todayIso().slice(0, 8) + '01');
  const [to, setTo] = useState<string | null>(todayIso());
  const [data, setData] = useState<ReportData | null>(null);
  const [busy, setBusy] = useState(false);
  const params = def ? qs({ ...(def.params.includes('year') ? { year } : {}), ...(def.params.includes('period') ? { from, to } : {}) }) : '';
  useEffect(() => {
    setData(null);
    if (!def || def.downloadOnly) return;
    setBusy(true);
    api.get<ReportData>(`${api0}/${def.code}${params}`).then(setData).catch(e => toast(e.message, 'error')).finally(() => setBusy(false));
  }, [mod, code, params, !!def]);
  const open = (m: string, r?: string) => navigate('/reports' + qs({ m, r }));
  const download = (format: 'xlsx' | '') => window.open(api.authUrl(`${api0}/${def!.code}${params}${format ? (params ? '&' : '?') + 'format=' + format : ''}`), '_blank');
  const groups = useMemo(() => { const g: Record<string, ReportDef[]> = {}; list.forEach(d => { (g[d.group] = g[d.group] || []).push(d); }); return g; }, [list]);

  return (
    <div className="reports">
      <aside className="reports-nav">
        {mods.length > 1 && <div className="segmented reports-mods">{mods.map(m => <button key={m.code} className={mod === m.code ? 'active' : ''} onClick={() => open(m.code)}><Icon name={m.icon} size={14} />{m.label}</button>)}</div>}
        {Object.entries(groups).map(([g, items]) => (
          <div key={g} className="reports-group"><div className="nav-section">{g}</div>
            {items.map(d => <button key={d.code} className={'report-item' + (d.code === code ? ' active' : '')} onClick={() => open(mod, d.code)}><Icon name={d.downloadOnly ? 'download' : 'report'} size={15} /><span>{d.title}</span></button>)}
          </div>))}
        {catalog[mod] && list.length === 0 && <p className="muted small pad">Niciun raport disponibil pentru rolul dumneavoastră.</p>}
      </aside>
      <section className="reports-main">
        {!def ? <EmptyState icon="report" title="Alegeți un raport" text={`Rapoartele modulului ${mods.find(m => m.code === mod)!.label}: tabel pe ecran și export Excel. Fiecare utilizator vede datele la care are acces.`} /> : <>
          <div className="report-head">
            <div className="grow"><h2>{def.title}</h2><p className="muted small">{def.description}</p></div>
          </div>
          <div className="toolbar">
            {def.params.includes('year') && <select className="select" style={{ width: 110 }} value={year} onChange={e => setYear(Number(e.target.value))}>{[year0 + 1, year0, year0 - 1, year0 - 2, year0 - 3].map(y => <option key={y}>{y}</option>)}</select>}
            {def.params.includes('period') && <><span className="muted small">de la</span><div style={{ width: 150 }}><DatePicker value={from} onChange={setFrom} /></div><span className="muted small">până la</span><div style={{ width: 150 }}><DatePicker value={to} onChange={setTo} /></div></>}
            <span className="grow" />
            {data && <span className="muted small">{data.count} {data.count === 1 ? 'rând' : 'rânduri'}</span>}
            {def.downloadOnly ? <button className="btn primary" onClick={() => download('')}><Icon name="download" />Descarcă</button>
              : <button className="btn" disabled={!data} onClick={() => download('xlsx')}><Icon name="download" />Excel</button>}
          </div>
          {def.downloadOnly ? <div className="banner small"><Icon name="info" size={15} /><span>Acest raport se descarcă (nu are previzualizare).</span></div>
            : busy && !data ? <div className="loading"><span className="spinner" /></div>
            : data && (data.rows.length === 0 ? <EmptyState icon="report" title="Nimic de raportat" text="Nu există date pentru parametrii aleși." /> : (
              <div className="table-wrap report-table">
                <table className="table">
                  <thead><tr>{data.columns.map((c, i) => <th key={i} className={c.type !== 'text' ? 'r' : ''}>{c.label}</th>)}</tr></thead>
                  <tbody>{data.rows.map((r, i) => <tr key={i}>{data.columns.map((c, j) => <td key={j} className={c.type !== 'text' ? 'r num' : ''}>{fmt(r[j], c.type)}</td>)}</tr>)}</tbody>
                  {data.totals && <tfoot><tr>{data.columns.map((c, j) => <td key={j} className={c.type !== 'text' ? 'r num' : ''}><b>{j === 0 ? 'Total' : c.type === 'money' ? fmt(data.totals![j], c.type) : ''}</b></td>)}</tr></tfoot>}
                </table>
              </div>))}
        </>}
      </section>
    </div>
  );
}
